/**
 * Auth endpoints must never trigger the refresh-and-retry flow: a failed login or sign-in has no
 * session to refresh, and retrying a failed refresh would loop forever.
 */
export function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return ["/User/login", "/User/refresh", "/User/external-login"].some(endpoint => url.includes(endpoint));
}

export type Auth401Strategy = "pass-through" | "refresh-and-retry" | "give-up";

/**
 * Decides how a failed response should be handled with respect to auth:
 * - "pass-through": not a recoverable 401 (not a 401, no request config, or an
 *   auth endpoint) — reject as-is without signing out.
 * - "refresh-and-retry": a first 401 on a normal request — refresh the token and
 *   retry the original request once.
 * - "give-up": a 401 that already went through one retry — the session can't be
 *   recovered, so sign the user out.
 */
export function classify401(params: {
  isUnauthorized: boolean;
  hasConfig: boolean;
  url?: string;
  alreadyRetried: boolean;
}): Auth401Strategy {
  const { isUnauthorized, hasConfig, url, alreadyRetried } = params;

  if (!isUnauthorized || !hasConfig || isAuthEndpoint(url)) return "pass-through";
  if (alreadyRetried) return "give-up";
  return "refresh-and-retry";
}
